import { findSiteFromUrl } from '@zooplus/commons-lang';

import {
  Component,
  Customer,
  Platform,
  trackEvent,
  TRACKING_EVENT,
  TRACKING_PAGE,
  TrackingPage,
} from './tracker-listener';
/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    MSStream: any;
    opera: string;
  }
}

export interface TrackingDetail {
  page: TrackingPage;
  sid: string;
  domain: string;
  siteId: number;
  locale: string;
  currencyCode: string;
  pageLocation: string;
  platform: Platform;
  customer?: Customer;
  target?: Component;
}

export function useTracking() {
  const site = findSiteFromUrl();

  function trackPageView({ pageName, sid }: { pageName?: TrackingPage; sid: string | undefined }) {
    const details = getAutomaticTrackDetails(sid);
    trackEvent({
      event: TRACKING_EVENT.PAGE_VIEW,
      page: pageName ?? details.page,
      detail: {
        sid: details.sid,
        domain: details.domain,
        siteId: details.siteId,
        locale: details.locale,
        currencyCode: details.currencyCode,
        pageLocation: details.pageLocation,
        platform: details.platform,
      },
    });
  }

  function trackClick({
    pageName,
    sid,
    target,
  }: {
    pageName?: TrackingPage;
    sid: string | undefined;
    target?: Component;
  }) {
    const details = getAutomaticTrackDetails(sid);
    trackEvent({
      event: 'CLICK',
      page: pageName ?? details.page,
      detail: {
        sid: details.sid,
        domain: details.domain,
        siteId: details.siteId,
        locale: details.locale,
        currencyCode: details.currencyCode,
        pageLocation: details.pageLocation,
        platform: details.platform,
        target: target ?? { name: '', id: '' },
      },
    });
  }

  function trackInteraction({
    pageName,
    sid,
    components,
  }: {
    pageName?: TrackingPage;
    sid: string | undefined;
    components: Component[];
  }) {
    const details = getAutomaticTrackDetails(sid);
    trackEvent({
      event: 'INTERACTION',
      page: pageName ?? details.page,
      detail: {
        sid: details.sid,
        domain: details.domain,
        siteId: details.siteId,
        locale: details.locale,
        currencyCode: details.currencyCode,
        pageLocation: details.pageLocation,
        platform: details.platform,
        components: components,
      },
    });
  }

  function trackExperiment({
    featureKey,
    variationKey,
  }: {
    featureKey: string;
    variationKey: string;
  }) {
    const details = getAutomaticTrackDetails(undefined);
    trackEvent({
      event: TRACKING_EVENT.EXPERIMENT,
      page: details.page,
      detail: {
        sid: undefined,
        domain: details.domain,
        siteId: details.siteId,
        locale: details.locale,
        currencyCode: details.currencyCode,
        pageLocation: details.pageLocation,
        platform: details.platform,
        experiments: [
          {
            tool: 'OPTIMIZELY',
            id: featureKey,
            value: variationKey,
          },
        ],
      },
    });
  }

  function getPlatform(): Platform {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'IOS';
    }

    if (/android/i.test(userAgent)) {
      return 'ANDROID';
    }

    return 'WEB';
  }

  function getTrackingPage(): TrackingPage {
    const path = window.location.href;

    //switch case for different paths
    switch (true) {
      case path.includes('checkout/cart'):
        return TRACKING_PAGE.CHECKOUT_CART;
      case path.includes('/checkout/preview'):
        return TRACKING_PAGE.CHECKOUT_PREVIEW;
      case path.includes('/zooclub/dashboard'):
        return TRACKING_PAGE.LOYALTY_PROGRAM_DASHBOARD;
      case path.includes('/zooclub/overview'):
        return TRACKING_PAGE.LOYALTY_PROGRAM_LANDING_PAGE;
      case path.includes('/account/overview'):
        return TRACKING_PAGE.ACCOUNT;
      default:
        return TRACKING_PAGE.CHECKOUT_FINISH;
    }
  }

  function getAutomaticTrackDetails(sid: string | undefined): TrackingDetail {
    return {
      page: getTrackingPage(),
      sid: sid ?? '',
      domain: site?.domain ?? 'zooplus.com',
      siteId: site?.id ?? 3,
      locale: site?.locale ?? 'de-DE',
      currencyCode: site?.currency ?? 'EUR',
      pageLocation: window.location.href,
      platform: getPlatform(),
    };
  }

  return {
    getPlatform,
    getAutomaticTrackDetails,
    trackPageView,
    trackClick,
    trackInteraction,
    trackExperiment,
  };
}
